import { useCallback, useEffect, useState, type DragEvent } from 'react';
import { avisar } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import { Icone } from './Icone.tsx';
import { ErroDaApi, apiMarketing, type FundoEnviado, type PainelDosFundos, type PostDeMarketing } from '../lib/api.ts';
import { copiar } from '../lib/posts.ts';

/**
 * ⭐⭐ OS FUNDOS POR IA — a parte 3 da aba Marketing, 16/10/2026 (PRD §50.6).
 *
 * O Hub monta os prompts dos fundos no padrão que deu certo em 15/10/2026 (o
 * estilo base colado primeiro, depois um prompt por imagem, com a altura de
 * cada emenda), a pessoa leva ao ChatGPT e sobe as imagens de volta, uma por
 * slide e a do fecho.
 *
 * - ⛔ **O Hub não fala com IA nenhuma** (PRD §38.4): a tela só copia texto.
 * - ⭐ **O motivo grava na hora**, como a escada. Enquanto ninguém escolheu,
 *   vale a sugestão do Hub, e o primeiro "copiar" a grava: o prompt que saiu
 *   daqui foi o dela.
 * - ⭐ **As vagas seguem os slides GRAVADOS**: é neles que as imagens se
 *   penduram. Com texto por gravar, a tela avisa.
 * - ⛔ **O arquivo sobe com um nome fixo**: o nome que ele tem no computador
 *   não sai do navegador.
 * - ⚠️ **Remover pergunta antes**; trocar é escolher outro arquivo, e a escolha
 *   já é o pedido.
 *
 * ⛔⛔ **Fala só com `/api/marketing`** (PRD §7.1).
 */

const numero = (n: number) => n.toLocaleString('pt-BR');
const tamanho = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB` : `${numero(Math.max(1, Math.round(bytes / 1024)))} KB`);

/** A vaga de uma imagem: a miniatura, ou o espaço para enviar. */
function Vaga({
  nome,
  url,
  fundo,
  teto,
  tipos,
  ocupada,
  erro,
  aoEnviar,
  aoRemover,
}: {
  nome: string;
  url: string | null;
  fundo: FundoEnviado | null;
  teto: number;
  tipos: string[];
  ocupada: boolean;
  erro: string | undefined;
  aoEnviar: (arquivo: File) => void;
  aoRemover: () => void;
}) {
  const [arrastando, setArrastando] = useState(false);
  const escolher = (lista: FileList | null) => {
    const arquivo = lista?.[0];
    if (arquivo) aoEnviar(arquivo);
  };
  const soltar = (e: DragEvent) => {
    e.preventDefault();
    setArrastando(false);
    if (!ocupada) escolher(e.dataTransfer.files);
  };
  const tetoNaTela = `${Math.round(teto / 1024 / 1024)} MB`;
  const campo = (rotulo: string) => (
    <input
      type="file"
      accept={tipos.join(',')}
      disabled={ocupada}
      aria-label={`${rotulo}: ${nome}`}
      onChange={(e) => {
        escolher(e.target.files);
        e.target.value = '';
      }}
    />
  );

  return (
    <div
      className={`mkt-vaga${fundo ? ' cheia' : ''}${arrastando ? ' arrastando' : ''}${ocupada ? ' ocupada' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setArrastando(true);
      }}
      onDragLeave={() => setArrastando(false)}
      onDrop={soltar}
    >
      {fundo && url ? (
        <>
          <img className="mkt-vaga-img" src={url} alt={`Fundo enviado para ${nome}`} width={1080} height={1350} loading="lazy" />
          <p className="mkt-vaga-meta">
            <span className="dado">
              {fundo.tipo} · {fundo.largura} × {fundo.altura} · {tamanho(fundo.tamanho)}
            </span>
          </p>
          {fundo.avisos.map((a) => (
            <p key={a} className="mkt-vaga-aviso">
              <Icone nome="atencao" tamanho={13} /> <span>{a}</span>
            </p>
          ))}
          <div className="mkt-vaga-acoes">
            <label className="imv-bt p mkt-arquivo">
              <Icone nome="subir" tamanho={12} /> Trocar
              {campo('Trocar a imagem')}
            </label>
            <button type="button" className="imv-bt fantasma p perigo" disabled={ocupada} onClick={aoRemover}>
              <Icone nome="lixeira" tamanho={12} /> Remover
            </button>
          </div>
        </>
      ) : (
        <label className="mkt-vaga-vazia mkt-arquivo">
          <Icone nome="subir" tamanho={20} />
          <b>Enviar a imagem</b>
          <small>
            PNG, JPG ou WEBP, até {tetoNaTela}. Ou arraste o arquivo para cá.
          </small>
          {campo('Enviar a imagem')}
        </label>
      )}
      {ocupada && <span className="mkt-vaga-subindo">Enviando…</span>}
      {erro && <p className="erro mkt-vaga-erro">{erro}</p>}
    </div>
  );
}

export function FundosDoPost({
  post,
  versaoDoConteudo,
  conteudoPorGravar,
  aoExpirarSessao,
  aoMudar,
}: {
  post: PostDeMarketing;
  /** Muda a cada gravação do conteúdo: as vagas seguem os slides gravados. */
  versaoDoConteudo: number;
  conteudoPorGravar: boolean;
  aoExpirarSessao: () => void;
  /** ⭐ Parte 4: um fundo entrou, foi trocado ou saiu. A seção das artes relê. */
  aoMudar?: () => void;
}) {
  const [painel, setPainel] = useState<PainelDosFundos | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** A vaga que está subindo, ou `motivo` enquanto a escolha grava. */
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erroDaVaga, setErroDaVaga] = useState<Record<string, string>>({});
  const [aRemover, setARemover] = useState<{ peca: string; nome: string } | null>(null);

  const falhou = useCallback(
    (e: unknown, padrao: string) => {
      if (e instanceof ErroDaApi && e.status === 401) {
        aoExpirarSessao();
        return null;
      }
      return e instanceof Error ? e.message : padrao;
    },
    [aoExpirarSessao],
  );

  useEffect(() => {
    let vale = true;
    apiMarketing
      .fundos(post.id)
      .then((p) => {
        if (!vale) return;
        setPainel(p);
        setErro(null);
      })
      .catch((e) => vale && setErro(falhou(e, 'Não consegui carregar os fundos.')));
    return () => {
      vale = false;
    };
    /* O formato muda as vagas (o fecho só no carrossel); a versão do conteúdo, os slides. */
  }, [post.id, post.formato, versaoDoConteudo, falhou]);

  if (erro) {
    return (
      <section className="s-folha mkt-fundos" aria-label="Fundos do post">
        <header className="s-folha-topo">
          <h2 className="s-folha-titulo">Fundos</h2>
        </header>
        <p className="erro mkt-fundos-corpo">{erro}</p>
      </section>
    );
  }
  if (!painel) return null;

  const p = painel.prompts;
  const motivoNaTela = painel.motivo ?? painel.sugestao?.motivo ?? painel.catalogo[0]?.chave ?? '';
  const doCatalogo = painel.catalogo.find((m) => m.chave === motivoNaTela);
  const sug = painel.sugestao;
  const repeteVizinho =
    painel.motivo && sug ? [sug.anterior, sug.seguinte].find((v) => v && v.motivo === painel.motivo) ?? null : null;
  const comFundo = painel.imagens.filter((v) => v.fundo);

  async function escolher(motivo: string, silencioso = false) {
    setOcupado('motivo');
    try {
      const novo = await apiMarketing.escolherMotivo(post.id, motivo);
      setPainel(novo);
      if (!silencioso) avisar(`Motivo dos fundos: ${novo.catalogo.find((m) => m.chave === motivo)?.nome ?? motivo}.`);
      return true;
    } catch (e) {
      const m = falhou(e, 'Não consegui gravar o motivo.');
      if (m) avisar(m, 'ruim');
      return false;
    } finally {
      setOcupado(null);
    }
  }

  /** ⭐ Copiar: se o motivo ainda era só sugestão, ele fica gravado, porque foi o prompt dele que saiu daqui. */
  async function copiarPrompt(texto: string, mensagem: string) {
    if (!painel) return;
    if (!painel.motivo && sug) await escolher(sug.motivo, true);
    copiar(texto, avisar, mensagem);
  }

  async function enviar(peca: string, nome: string, arquivo: File) {
    if (!painel) return;
    setErroDaVaga((x) => ({ ...x, [peca]: '' }));
    /* A conferência de verdade é do servidor, pelo conteúdo; aqui só se poupa a espera. */
    if (arquivo.size > painel.envio.teto) {
      setErroDaVaga((x) => ({ ...x, [peca]: `A imagem passa de ${Math.round(painel.envio.teto / 1024 / 1024)} MB. Salve menor e envie de novo.` }));
      return;
    }
    setOcupado(peca);
    try {
      const novo = await apiMarketing.enviarFundo(post.id, peca, arquivo);
      setPainel(novo);
      aoMudar?.();
      avisar(`Fundo de ${nome.toLowerCase() === 'a imagem' ? 'a imagem' : nome} enviado.`);
    } catch (e) {
      const m = falhou(e, 'Não consegui enviar a imagem.');
      if (m) setErroDaVaga((x) => ({ ...x, [peca]: m }));
    } finally {
      setOcupado(null);
    }
  }

  async function remover(peca: string) {
    setOcupado(peca);
    try {
      setPainel(await apiMarketing.removerFundo(post.id, peca));
      aoMudar?.();
      avisar('Fundo removido.');
    } catch (e) {
      const m = falhou(e, 'Não consegui remover o fundo.');
      if (m) setErroDaVaga((x) => ({ ...x, [peca]: m }));
    } finally {
      setOcupado(null);
    }
  }

  const vaga = (peca: string, nome: string, fundo: FundoEnviado | null) => (
    <Vaga
      nome={nome}
      url={fundo ? apiMarketing.imagemDoFundo(post.id, peca, fundo.id) : null}
      fundo={fundo}
      teto={painel.envio.teto}
      tipos={painel.envio.tipos}
      ocupada={ocupado === peca}
      erro={erroDaVaga[peca] || undefined}
      aoEnviar={(arquivo) => void enviar(peca, nome, arquivo)}
      aoRemover={() => setARemover({ peca, nome })}
    />
  );

  return (
    <section className="s-folha mkt-fundos" aria-label="Fundos do post">
      <header className="s-folha-topo mkt-conteudo-topo">
        <div>
          <h2 className="s-folha-titulo">Fundos</h2>
          <p className="mkt-nota">O Hub monta os prompts; a imagem se gera no ChatGPT e volta para cá, uma por vaga.</p>
        </div>
        {p && (
          <button type="button" className="imv-bt" onClick={() => void copiarPrompt(p.tudo, 'Todos os prompts copiados, na ordem.')}>
            <Icone nome="copiar" tamanho={14} /> Copiar tudo na ordem
          </button>
        )}
      </header>

      <div className="mkt-fundos-corpo">
        {post.formato === 'texto' ? (
          <p className="mkt-vazio-bloco">Post só de texto: não leva fundo.</p>
        ) : (
          <>
            <div className="mkt-fundos-topo">
              <div className="mkt-motivo">
                <div className="imv-campo">
                  <label className="imv-rotulo" htmlFor="mkt-motivo">
                    Motivo
                  </label>
                  <span className="imv-in">
                    <select id="mkt-motivo" value={motivoNaTela} disabled={ocupado === 'motivo'} onChange={(e) => void escolher(e.target.value)}>
                      {painel.catalogo.map((m) => (
                        <option key={m.chave} value={m.chave}>
                          {m.nome}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>
                {doCatalogo && <p className="mkt-motivo-desc">{doCatalogo.descricao}</p>}
                {!painel.motivo && sug && (
                  <p className="mkt-sugerido">
                    <span className="mkt-selo">Sugestão do Hub</span>
                    {sug.anterior ? (
                      <>
                        {' '}
                        O post anterior, <q>{sug.anterior.titulo}</q>, usou {sug.anterior.nome.toLowerCase()}
                        {sug.seguinte ? <>, e o seguinte, <q>{sug.seguinte.titulo}</q>, {sug.seguinte.nome.toLowerCase()}</> : null}.
                      </>
                    ) : (
                      ' Nenhum post antes deste escolheu motivo.'
                    )}{' '}
                    Fica gravada ao copiar o primeiro prompt, ou ao escolher outro motivo na lista.
                  </p>
                )}
                {repeteVizinho && (
                  <p className="mkt-aviso">
                    Mesmo motivo de <q>{repeteVizinho.titulo}</q>, que fica ao lado no feed.
                  </p>
                )}
                {p?.coresDosNiveis && (
                  <p className="mkt-nota">
                    Tema dos níveis: a cor de cada nível vai no próprio material, nunca como luz (
                    {painel.imagens
                      .filter((v) => v.nivel)
                      .map((v) => `${v.nome.toLowerCase()}, ${v.nivel!.hex}`)
                      .join('; ')}
                    ).
                  </p>
                )}
              </div>

              <div className="mkt-como">
                <h3 className="mkt-bloco-titulo">Como usar</h3>
                {p?.continuo ? (
                  <ol>
                    <li>Abra uma conversa nova no ChatGPT.</li>
                    <li>Cole o estilo base e espere a resposta "ok".</li>
                    <li>Cole um prompt por vez, na ordem, sempre na mesma conversa: é ela que guarda a imagem anterior, e a emenda depende disso.</li>
                    <li>Baixe cada imagem e envie na vaga dela, aqui embaixo.</li>
                  </ol>
                ) : (
                  <ol>
                    <li>Abra uma conversa nova no ChatGPT.</li>
                    <li>Cole o prompt.</li>
                    <li>Baixe a imagem e envie na vaga, aqui embaixo.</li>
                  </ol>
                )}
                <p className="mkt-nota">O prompt não leva texto do post: só a cena. O texto, o logotipo e a numeração entram na montagem.</p>
              </div>
            </div>

            {conteudoPorGravar && <p className="mkt-aviso">Há texto do conteúdo por gravar. Os prompts e as vagas seguem os slides gravados.</p>}

            {!p && <p className="mkt-vazio-bloco">Grave os slides do conteúdo antes: o número de fundos sai deles.</p>}

            {/* ⭐ As emendas: as imagens lado a lado, sem fresta, para ver se o panorama fecha. */}
            {p?.continuo && comFundo.length >= 2 && (
              <div className="mkt-emendas">
                <h3 className="mkt-bloco-titulo">As emendas</h3>
                <div className="mkt-emendas-tira" role="img" aria-label={`As ${comFundo.length} imagens enviadas, lado a lado, na ordem do carrossel`}>
                  {painel.imagens.map((v) =>
                    v.fundo ? (
                      <img key={v.peca} src={apiMarketing.imagemDoFundo(post.id, v.peca, v.fundo.id)} alt="" />
                    ) : (
                      <span key={v.peca} className="mkt-emendas-falta">
                        {v.numero}
                      </span>
                    ),
                  )}
                </div>
                <p className="mkt-nota">A borda direita de cada imagem precisa continuar na esquerda da seguinte. Pequenas diferenças a montagem acerta.</p>
              </div>
            )}

            {p && (
              <ol className="mkt-fundos-lista">
                {p.estiloBase && (
                  <li className="mkt-fundo mkt-fundo-base">
                    <div className="mkt-fundo-texto">
                      <div className="mkt-slide-topo">
                        <span className="mkt-slide-num dado">0</span>
                        <b>Estilo base</b>
                        <span className="mkt-fundo-de">cole primeiro</span>
                      </div>
                      <span className="imv-in lr-texto">
                        <textarea readOnly rows={8} value={p.estiloBase} aria-label="O estilo base" />
                      </span>
                      <div className="mkt-fundo-acoes">
                        <button type="button" className="imv-bt primario p" onClick={() => void copiarPrompt(p.estiloBase!, 'Estilo base copiado.')}>
                          <Icone nome="copiar" tamanho={12} /> Copiar o estilo base
                        </button>
                      </div>
                    </div>
                  </li>
                )}
                {painel.imagens.map((v) => (
                  <li key={v.peca} className="mkt-fundo">
                    <div className="mkt-fundo-texto">
                      <div className="mkt-slide-topo">
                        <span className="mkt-slide-num dado">{v.numero}</span>
                        <b>{v.nome}</b>
                        <span className="mkt-fundo-de">
                          imagem {v.numero} de {p.total}
                        </span>
                        {v.nivel && (
                          <span className="mkt-fundo-nivel">
                            {v.nivel.nome} · <span className="dado">{v.nivel.hex}</span>
                          </span>
                        )}
                      </div>
                      {v.titulo && <p className="mkt-fundo-titulo">{v.titulo}</p>}
                      <span className="imv-in lr-texto">
                        <textarea readOnly rows={5} value={v.prompt} aria-label={`O prompt da imagem ${v.numero}`} />
                      </span>
                      <div className="mkt-fundo-acoes">
                        <button type="button" className="imv-bt primario p" onClick={() => void copiarPrompt(v.prompt, `Prompt da imagem ${v.numero} copiado.`)}>
                          <Icone nome="copiar" tamanho={12} /> Copiar o prompt
                        </button>
                        {v.ajusteDaEmenda && (
                          <button
                            type="button"
                            className="imv-bt fantasma p"
                            title="Para quando a imagem não continuar a anterior: cole na mesma conversa."
                            onClick={() => void copiarPrompt(v.ajusteDaEmenda!, `Ajuste da emenda da imagem ${v.numero} copiado.`)}
                          >
                            <Icone nome="copiar" tamanho={12} /> Se não emendar
                          </button>
                        )}
                      </div>
                    </div>
                    {vaga(v.peca, v.nome, v.fundo)}
                  </li>
                ))}
              </ol>
            )}

            {p && (
              <details className="mkt-ajustes">
                <summary>Se a imagem não sair como o pedido</summary>
                <ul>
                  {p.ajustes.map((a) => (
                    <li key={a.quando}>
                      <span>{a.quando}</span>
                      <button type="button" className="imv-bt p" onClick={() => void copiarPrompt(a.texto, 'Pedido de ajuste copiado.')}>
                        <Icone nome="copiar" tamanho={12} /> Copiar o pedido
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="mkt-nota">Cole na mesma conversa, logo depois da imagem que saiu errada.</p>
              </details>
            )}
          </>
        )}

        {painel.guardadosFora.length > 0 && (
          <div className="mkt-guardados">
            <h3 className="mkt-bloco-titulo">Guardados fora do plano</h3>
            <p className="mkt-nota">Imagens de vagas que o formato de agora não tem. Não entram na montagem; ficam até alguém remover.</p>
            <ul>
              {painel.guardadosFora.map((g) => (
                <li key={g.peca}>
                  <span>{g.nome}</span>
                  {vaga(g.peca, g.nome, g.fundo)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {aRemover && (
        <Confirmar
          titulo={`Remover o fundo: ${aRemover.nome}`}
          descricao="A imagem sai do Hub, a original e a de 1080 × 1350. Para ter de volta, será preciso enviá-la de novo."
          rotuloAcao="Remover"
          aoFechar={() => setARemover(null)}
          aoConfirmar={() => {
            const peca = aRemover.peca;
            setARemover(null);
            void remover(peca);
          }}
        />
      )}
    </section>
  );
}
